import React, { useState, useRef, useEffect } from 'react'
import { SendIcon } from '@/components/Icons'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '../ui/button'
import { useChat } from '@/hooks/useChat'
import { usePresence } from '@/hooks/usePresence'
import type { User } from '@/schemas/chat.schema'
import {
  CHAT_LABELS,
  CHAT_ERRORS,
  formatChatTime,
  groupMessagesByDate,
  formatDateSeparator,
  CHAT_CONFIG,
  CHAT_OBSERVER_OPTIONS,
} from '@/constants/chat'
import { AlertCircle, Ban } from 'lucide-react'
import {
  getChatUserData,
  handleChatIntersection,
  initializeChatRoom,
  syncChatUserData,
} from '@/utils/chatUtils'
import { tokenService } from '@/lib/token.service'
import DateSeparator from '../chat/DateSeparator'
import MessageBubble from '../chat/MessageBubble'
import TypingLoader from '../chat/TypingLoader'
import { useUserRole } from '@/contexts/UserRoleContext'

interface MessageAdminDataProps {
  roomId: string
  currentUser: User
  otherUserId: string
  otherUserName?: string
  otherUserAvatar?: string
  isSSAdmin?: boolean
}

export default function MessageAdminData({
  roomId,
  currentUser,
  otherUserId,
  otherUserName,
  otherUserAvatar,
  isSSAdmin = false,
}: Readonly<MessageAdminDataProps>) {
  const [message, setMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const initialMarkedRef = useRef(false)
  const { currentUser: localUser } = useUserRole()

  const [otherUserData, setOtherUserData] = useState<{
    name: string
    avatar: string | null
  }>({
    name: otherUserName || `User ${otherUserId.slice(-4)}`,
    avatar: otherUserAvatar || null,
  })
  const [isLoadingUserData, setIsLoadingUserData] = useState(isSSAdmin)
  const [userDataError, setUserDataError] = useState<string | null>(null)

  const tokenUser = tokenService.getUser()
  const currentUserData = {
    name: `${tokenUser.firstName} ${tokenUser.lastName}`,
    avatar: tokenUser.profilePicture?.url || null,
  }

  const localStorageChatAdminId = !isSSAdmin
    ? localUser?.chatAdminId
    : undefined

  useEffect(() => {
    const fetchOtherUser = async () => {
      try {
        setIsLoadingUserData(true)
        setUserDataError(null)

        const userData = await getChatUserData(otherUserId)

        if (userData) {
          setOtherUserData({
            name: userData.name,
            avatar: userData.profilePicture?.url || null,
          })
          setIsLoadingUserData(false)
          syncChatUserData(otherUserId)
        } else {
          setUserDataError('User not found. Cannot initialize chat.')
          setIsLoadingUserData(false)
        }
      } catch (error) {
        console.error('Error fetching user data:', error)
        setUserDataError('Failed to load user data. Cannot initialize chat.')
        setIsLoadingUserData(false)
      }
    }

    if (isSSAdmin) {
      fetchOtherUser()
    }
  }, [otherUserId, isSSAdmin])

  const {
    messages,
    loading,
    error,
    typing,
    sendMessage: sendFirebaseMessage,
    setTypingIndicator,
    roomExists,
    isAuthorized,
    authError,
    markAsRead,
  } = useChat(roomId, currentUser, isSSAdmin, localStorageChatAdminId)

  const { otherUserOnline, lastSeen } = usePresence(
    roomId,
    currentUser,
    otherUserId
  )

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    if (initialMarkedRef.current) return
    if (loading) return

    initialMarkedRef.current = true
    markAsRead()
  }, [loading])

  useEffect(() => {
    const initRoom = async () => {
      if (isSSAdmin && (isLoadingUserData || userDataError)) {
        return
      }

      try {
        const chatAdminId = isSSAdmin ? currentUser.id : localUser?.chatAdminId
        await initializeChatRoom(
          roomId,
          [currentUser.id, otherUserId],
          chatAdminId
        )
      } catch (err) {
        console.error('Failed to initialize chat room:', err)
      }
    }
    initRoom()
  }, [
    roomId,
    currentUser.id,
    otherUserId,
    isSSAdmin,
    isLoadingUserData,
    userDataError,
    localUser,
  ])

  useEffect(() => {
    if (CHAT_CONFIG.AUTO_SCROLL) {
      scrollToBottom()
    }
  }, [messages])

  useEffect(() => {
    if (
      !roomId ||
      !CHAT_CONFIG.SHOW_READ_RECEIPTS ||
      !messagesContainerRef.current
    )
      return

    const observer = new IntersectionObserver(
      entries => handleChatIntersection(roomId, entries),
      CHAT_OBSERVER_OPTIONS
    )

    // Observe after paint to avoid missing nodes due to render timing.
    let rafId: number | null = null

    const observeNow = () => {
      const container = messagesContainerRef.current
      if (!container) return
      const els = container.querySelectorAll<HTMLElement>('[data-message-id]')

      if (els.length === 0) {
        // retry once on next frame (covers small render delays / animations)
        rafId = requestAnimationFrame(() => {
          const retry =
            container.querySelectorAll<HTMLElement>('[data-message-id]')
          retry.forEach(el => observer.observe(el))
        })
      } else {
        els.forEach(el => observer.observe(el))
      }
    }

    rafId = requestAnimationFrame(observeNow)

    return () => {
      if (rafId != null) cancelAnimationFrame(rafId)
      observer.disconnect()
    }
  }, [roomId, messages])

  const handleSendMessage = async () => {
    if (message.trim()) {
      await sendFirebaseMessage(message.trim())
      setMessage('')

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
      }
      await setTypingIndicator(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleInputChange = async (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value
    setMessage(newValue)

    if (CHAT_CONFIG.SHOW_TYPING_INDICATOR) {
      if (newValue.trim()) {
        await setTypingIndicator(true)

        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current)
        }

        typingTimeoutRef.current = setTimeout(async () => {
          await setTypingIndicator(false)
        }, CHAT_CONFIG.TYPING_DEBOUNCE)
      } else {
        await setTypingIndicator(false)
      }
    }
  }

  const groupedMessages = groupMessagesByDate(messages)

  const getOnlineStatusText = () => {
    if (otherUserOnline) {
      return CHAT_LABELS.COMMON.ONLINE
    }
    if (lastSeen) {
      return CHAT_LABELS.COMMON.LAST_SEEN.replace(
        '{time}',
        formatChatTime(lastSeen)
      )
    }
    return CHAT_LABELS.COMMON.OFFLINE
  }

  const isError = !loading && error
  const isEmpty = !loading && !error && messages?.length === 0
  const hasMessages = !loading && !error && messages?.length > 0
  const isChatDisabled = !roomExists || !!userDataError || !isAuthorized
  const isInitializing = isLoadingUserData

  const displayError = authError || userDataError

  return (
    <>
      {/* Chat Container */}
      <section
        aria-label="Chat Interface"
        className="flex-1 mx-4 mb-4 bg-white rounded-xl border border-ss-neutral-50 flex flex-col overflow-hidden"
      >
        {/* Chat Header */}
        <div className="px-4 py-3 border-b border-ss-neutral-50">
          <div className="flex items-center gap-3">
            {isInitializing ? (
              <>
                <div className="h-11 w-11 rounded-full bg-ss-neutral-100 animate-pulse" />
                <div className="flex-1">
                  <div className="h-4 w-32 bg-ss-neutral-100 rounded animate-pulse mb-2" />
                  <div className="h-3 w-20 bg-ss-neutral-100 rounded animate-pulse" />
                </div>
              </>
            ) : (
              <>
                <Avatar className="h-11 w-11">
                  <AvatarImage
                    src={otherUserData?.avatar ?? ''}
                    alt={otherUserData.name}
                  />
                  <AvatarFallback className="bg-ss-neutral-100 text-ss-neutral-600 font-medium">
                    {otherUserData.name
                      .split(' ')
                      .map(n => n[0])
                      .join('')}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h2 className="font-semibold text-base text-ss-neutral-950">
                    {otherUserData.name}
                  </h2>
                  <div className="flex items-center gap-1.5">
                    {otherUserOnline && !isChatDisabled && (
                      <div className="size-2 bg-green-500 rounded-full flex-shrink-0" />
                    )}
                    <p className="text-xs text-ss-neutral-500">
                      {isChatDisabled ? 'Unavailable' : getOnlineStatusText()}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Chat Unavailable Banner */}
        {isChatDisabled && !isInitializing && (
          <div className="px-4 py-3 bg-ss-warning-50 border-b border-ss-warning-100">
            <div className="flex items-center gap-2 text-ss-warning-700">
              <Ban className="size-4 flex-shrink-0" />
              <p className="text-sm font-medium">
                {displayError ||
                  'This conversation is no longer available. The message admin may have been changed. Please refresh the page and try again.'}
              </p>
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div
          ref={messagesContainerRef}
          className="flex flex-col-reverse p-4 overflow-y-auto h-[calc(100vh-320px)] sm:h-[calc(100vh-300px)]"
        >
          <div ref={messagesEndRef} />

          {isInitializing && (
            <div className="flex flex-col gap-4 h-full justify-end">
              {[1, 2, 3].map(i => (
                <div
                  key={i}
                  className={`flex gap-2 ${i % 2 === 0 ? 'flex-row-reverse' : ''}`}
                >
                  <div className="size-8 rounded-full bg-ss-neutral-100 animate-pulse flex-shrink-0" />
                  <div className="space-y-2">
                    <div
                      className={`h-3 ${i % 2 === 0 ? 'w-48' : 'w-64'} bg-ss-neutral-100 rounded animate-pulse`}
                    />
                    <div
                      className={`h-3 ${i % 2 === 0 ? 'w-32' : 'w-40'} bg-ss-neutral-100 rounded animate-pulse`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isInitializing && loading && (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-ss-neutral-500">
                {CHAT_LABELS.COMMON.LOADING}
              </p>
            </div>
          )}

          {!isInitializing && isError && (
            <div className="flex items-center justify-center h-full gap-2 text-destructive">
              <AlertCircle className="size-4" />
              <p className="text-sm">{error || CHAT_ERRORS.LOAD_FAILED}</p>
            </div>
          )}

          {!isInitializing && isEmpty && !isChatDisabled && (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm text-ss-neutral-500">
                No messages yet. Start a conversation!
              </p>
            </div>
          )}

          {!isInitializing && hasMessages && (
            <>
              {Array.from(groupedMessages.entries())
                .reverse()
                .map(([date, msgs]) => (
                  <React.Fragment key={date}>
                    {msgs.reverse().map(msg => {
                      const isOwnMessage = msg.senderId === currentUser.id
                      return (
                        <MessageBubble
                          key={msg.id}
                          messageId={msg.id}
                          text={msg.text}
                          timestamp={msg.timestamp}
                          senderName={
                            isSSAdmin ? msg.senderName : 'SlopeSync Admin'
                          }
                          senderAvatar={
                            isOwnMessage
                              ? currentUserData.avatar || undefined
                              : otherUserData.avatar || undefined
                          }
                          isOwnMessage={isOwnMessage}
                          read={msg.read || false}
                        />
                      )
                    })}
                    <DateSeparator
                      date={formatDateSeparator(msgs[0].timestamp)}
                    />
                  </React.Fragment>
                ))}
            </>
          )}
        </div>

        {/* Message Input */}
        <div className="border-t border-ss-neutral-50 relative">
          <div className="flex items-end gap-2 p-3 mr-2 relative">
            {/* Typing indicator */}
            {typing && CHAT_CONFIG.SHOW_TYPING_INDICATOR && !isChatDisabled && (
              <div className="absolute -top-9 left-4 z-10">
                <div className="flex items-center gap-2 px-3 py-2">
                  <Avatar className="h-6 w-6 flex-shrink-0">
                    {otherUserData.avatar && (
                      <AvatarImage
                        src={otherUserData.avatar}
                        alt={otherUserData.name}
                      />
                    )}
                    <AvatarFallback className="text-[10px] bg-ss-neutral-100 text-ss-neutral-600">
                      {otherUserData.name
                        .split(' ')
                        .map(n => n[0])
                        .join('')}
                    </AvatarFallback>
                  </Avatar>
                  <TypingLoader />
                  <span className="text-xs text-ss-neutral-600">
                    {isSSAdmin ? otherUserData.name.split(' ')[0] : 'Admin'} is
                    typing...
                  </span>
                </div>
              </div>
            )}

            <Textarea
              ref={inputRef}
              value={message}
              onChange={handleInputChange}
              onKeyDown={handleKeyPress}
              placeholder={
                isChatDisabled ? 'Chat unavailable' : 'Type your message…'
              }
              className="flex-1 border-none bg-transparent text-sm text-ss-neutral-950 placeholder:text-ss-neutral-400 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none disabled:opacity-50 disabled:cursor-not-allowed resize-none overflow-y-auto min-h-[40px] max-h-[120px] py-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] [field-sizing:content]"
              aria-label="Type your message"
              disabled={isChatDisabled}
              rows={1}
            />

            <div className="flex items-center gap-4 py-2">
              {' '}
              <Button
                size="icon"
                variant="ghost"
                className={`p-0 size-4 ${isChatDisabled || !message.trim() ? 'cursor-not-allowed' : 'hover:bg-ss-action-gray-50'}`}
                aria-label="Send message"
                title={
                  isChatDisabled
                    ? displayError || 'Chat unavailable'
                    : !message.trim()
                      ? 'Type a message to send'
                      : 'Send message'
                }
                onClick={() => handleSendMessage()}
                disabled={isChatDisabled || !message.trim()}
                aria-disabled={isChatDisabled || !message.trim()}
              >
                {/* inner span gives a more pronounced disabled style without changing Button internals */}
                <span
                  className={`inline-flex items-center justify-center h-8 px-2 rounded transition-colors 
                    ${
                      isChatDisabled || !message.trim()
                        ? 'bg-ss-neutral-100 text-ss-neutral-400'
                        : 'bg-ss-blue-500 text-white'
                    }`}
                  aria-hidden="true"
                >
                  <SendIcon
                    className={`size-4 ${isChatDisabled || !message.trim() ? 'grayscale opacity-60' : ''}`}
                  />
                </span>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
